import React from 'react';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, trend, color = '#3b82f6' }) => {
  return (
    <div style={{
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <div>
          <div style={{
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '8px',
            fontWeight: '500'
          }}>
            {title}
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#1e293b'
          }}>
            {value}
          </div>
          {subtitle && (
            <div style={{
              fontSize: '12px',
              color: '#9ca3af',
              marginTop: '4px'
            }}>
              {subtitle}
            </div>
          )}
        </div>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '8px',
          backgroundColor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px'
        }}>
          {icon}
        </div>
      </div>
      {trend && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '14px'
        }}>
          <span style={{
            color: trend.isPositive ? '#10b981' : '#ef4444',
            fontWeight: '500'
          }}>
            {trend.isPositive ? '↑' : '↓'} {Math.abs(trend.value)}%
          </span>
          <span style={{ color: '#6b7280' }}>vs last period</span>
        </div>
      )}
    </div>
  );
};

interface QuickStatsProps {
  stats: {
    totalValue: number;
    cashBalance: number;
    totalPositions: number;
    openOrders: number;
    upcomingDividends: number;
  };
}

const QuickStats: React.FC<QuickStatsProps> = ({ stats }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '20px',
      marginBottom: '24px'
    }}>
      <StatCard
        title="Portfolio Value"
        value={formatCurrency(stats.totalValue)}
        subtitle="Total account value"
        icon="💰"
        color="#10b981"
      />
      <StatCard
        title="Cash Balance"
        value={formatCurrency(stats.cashBalance)}
        subtitle="Available to trade"
        icon="💵"
        color="#3b82f6"
      />
      <StatCard
        title="Open Positions"
        value={stats.totalPositions}
        subtitle="Active holdings"
        icon="📊"
        color="#8b5cf6"
      />
      <StatCard
        title="Pending Orders"
        value={stats.openOrders}
        subtitle="Awaiting execution"
        icon="📋"
        color="#f59e0b"
      />
      <StatCard
        title="Upcoming Dividends"
        value={stats.upcomingDividends}
        subtitle="Next 30 days"
        icon="📅"
        color="#ec4899"
      />
    </div>
  );
};

export default QuickStats;
